import React, { FormEvent } from 'react';
import { Instagram, Linkedin } from "lucide-react";
import { useToast } from "../../hooks/use-toast";

const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  const { toast } = useToast();
  const email = (event.target as HTMLFormElement).elements.namedItem('email-address') as HTMLInputElement;

  try {
    // Post the email to your API route
    const response = await fetch('/api/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.value }),
    });

    if (response.ok) {
      toast({
        title: "Successfully subscribed!",
        description: "Thank you for subscribing to our newsletter.",
        variant: "default",
      });
      // Clear the input after successful subscription
      email.value = '';
    } else {
      const errorData = await response.json();
      toast({
        title: "Subscription failed",
        description: errorData.error || "There was an error subscribing. Please try again.",
        variant: "destructive",
      });
    }
  } catch (error) {
    toast({
      title: "Error",
      description: "There was a problem connecting to the server. Please try again later.",
      variant: "destructive",
    });
  }
};

const navigation = {
  product: [
    { name: 'How it Works', href: '/how-it-works' },
    { name: 'Pricing', href: '/pricing' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
  ],
  social: [
    { name: 'Instagram', href: '#', icon: Instagram },
    { name: 'LinkedIn', href: '#', icon: Linkedin },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-background border-t" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto max-w-7xl px-6 py-12 w-full">
        {/* Brand Section - Standalone above everything */}
        <div className="mb-8">
          <h3 className="text-2xl font-semibold text-foreground">MeetMemoize</h3>
          <p className="mt-2 text-base text-muted-foreground md:whitespace-nowrap">
            Transform your meetings into actionable insights with AI-powered analysis.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 mx-0 gap-4">
          {/* Product Section */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-foreground mb-3">Product</h3>
            <ul className="space-y-2">
              {navigation.product.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Section */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-foreground mb-3">Company</h3>
            <ul className="space-y-2">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Section */}
          <div className="col-span-1">
            <h3 className="text-sm font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              {navigation.legal.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Section */}
          <div className="col-span-2 md:col-span-2 mt-8 md:mt-0">
            <h3 className="text-sm font-semibold text-foreground mb-2">Subscribe to our updates</h3>
            <p className="text-sm text-muted-foreground mb-4">
              The latest updates and resources, sent directly to your inbox.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email-address"
                id="email-address"
                autoComplete="email"
                required
                placeholder="Enter your email"
                className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 border-t pt-8">
          <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground text-center sm:text-left">
              &copy; 2025 MeetMemoize. All rights reserved.
            </p>
            <div className="flex justify-center space-x-6">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}